import type { SceneCopy } from './scenes/scene-markup';

export type OnboardingVariant = 'returning' | 'new';

export type OnboardingCopy = {
  dialogLabel: string;
  title: (firstName: string) => string;
  // Two lines: who gave the Vault, and what is in it.
  subtitle: (provider: string | null, hasMemories: boolean) => [string, string];
  firstAction: string;
  lastAction: string;
  scenes: SceneCopy[];
};

const VAULT_SCENE: SceneCopy = {
  id: 'vault',
  title: 'All together. Easy to find again.',
  subtitle:
    'Your Vault keeps everything in one place, with Albums for the people, places, and moments you want to revisit.',
};

const TIMELINE_SCENE: SceneCopy = {
  id: 'timeline',
  title: 'Your lifetime, in perspective.',
  subtitle:
    'Explore memories across the years in Timeline—from everyday moments to the milestones that shaped a life.',
};

// Someone who can't add memories (a viewer) isn't asked to.
const VIEWER_PRESERVE_SCENE: SceneCopy = {
  id: 'preserve',
  title: 'Many ways to keep what matters.',
  subtitle:
    'Photos, audio, documents, and memories in their own words, all kept together.',
};

const COPY: Record<OnboardingVariant, OnboardingCopy> = {
  // Existing customers, the first time they open the new site.
  returning: {
    dialogLabel: 'Welcome back',
    title: (firstName) =>
      firstName ? `Welcome back, ${firstName}.` : 'Welcome back.',
    subtitle: (provider, hasMemories) => [
      provider
        ? `${provider} gave you a place to preserve your story.`
        : 'You have a place to preserve your story.',
      hasMemories
        ? 'The memories you saved are still here.'
        : 'Your Vault is ready for your first memory.',
    ],
    firstAction: 'See what’s new',
    lastAction: 'Continue to Memories',
    scenes: [
      {
        id: 'preserve',
        title: 'More ways to keep what matters.',
        subtitle:
          'Now add photos, audio, documents, or a memory in your own words—with Forever Storage included.',
      },
      VAULT_SCENE,
      TIMELINE_SCENE,
    ],
  },
  // Accounts created after the new site launched, owners and invitees alike.
  // Draft wording, pending the client's approval.
  new: {
    dialogLabel: 'Welcome',
    title: (firstName) => (firstName ? `Welcome, ${firstName}.` : 'Welcome.'),
    subtitle: (provider, hasMemories) => [
      provider
        ? `${provider} has set up a place to preserve your story.`
        : 'This is a place to preserve your story.',
      hasMemories
        ? 'Here are some of the memories already in it.'
        : 'Your Vault is ready for your first memory.',
    ],
    firstAction: 'Get started',
    lastAction: 'Continue to Memories',
    scenes: [
      {
        id: 'preserve',
        title: 'Many ways to keep what matters.',
        subtitle:
          'Add photos, audio, documents, or a memory in your own words—with Forever Storage included.',
      },
      VAULT_SCENE,
      TIMELINE_SCENE,
    ],
  },
};

export const onboardingCopy = (
  variant: OnboardingVariant,
  canAddMemories: boolean,
): OnboardingCopy => {
  const copy = COPY[variant];
  if (canAddMemories) return copy;
  return {
    ...copy,
    // An empty Vault isn't "ready for your first memory" to someone who
    // can't add one.
    subtitle: (provider, hasMemories) => {
      const [first, second] = copy.subtitle(provider, hasMemories);
      return [first, hasMemories ? second : 'Nothing has been added yet.'];
    },
    scenes: [VIEWER_PRESERVE_SCENE, ...copy.scenes.slice(1)],
  };
};
